import { useEffect, useState, useRef, useMemo } from "react"; // Added useMemo
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux"; // Added
import {
  setSegments,
  clearSegments,
} from "../../../store/redux_auth/authSlice"; // Added
import SegmentMenu from "../../../components/navegation/SegmentMenu";
import ModalNew from "../../../components/ui/popup/Modal";
import PopupHeader from "../../../components/ui/popup/common/popupHeader";
import PopupContent from "../../../components/ui/popup/common/PopupContent";
import PopupSection from "../../../components/ui/popup/common/PopupSection";
import createWebSocketClient from "../../../services/sockets/WebSocketService";
import SocketActions from "../../../lib/socketActions";
import FilingNotificationService from "../../../services/sockets/notifications/FilingNotificationService";
import { getUserRole } from "../../../utils/auth";
import { fetchCurrentFiling } from "../../../api/filings/filingAPI";
import { identity } from "lodash";

// Icon mapping from Material Icon names to SVG paths
const iconMap = {
  summarize: {
    normal: `${process.env.PUBLIC_URL}/images/icons/filing_dark_grey.svg`,
    active: `${process.env.PUBLIC_URL}/images/icons/filing_blue.svg`,
  },
  identity: {
    normal: `${process.env.PUBLIC_URL}/images/icons/user_segment_grey.svg`,
    active: `${process.env.PUBLIC_URL}/images/icons/user_blue.svg`,
  },
  sms: {
    normal: `${process.env.PUBLIC_URL}/images/icons/message_grey.svg`,
    active: `${process.env.PUBLIC_URL}/images/icons/message_blue.svg`,
  },
  upload: {
    normal: `${process.env.PUBLIC_URL}/images/icons/news_dark_grey.svg`,
    active: `${process.env.PUBLIC_URL}/images/icons/news_blue.svg`,
  },
  trending_up: {
    normal: `${process.env.PUBLIC_URL}/images/icons/survey_dark_grey.svg`,
    active: `${process.env.PUBLIC_URL}/images/icons/survey_blue.svg`,
  },
};

const serviceWidgetMap = {
  TX: [
    {
      componentName: "FilingOverview",
      service: "tax-filing/filing",
      route: "",
      slug: "Filing Hub",
      icon: "summarize",
    },
    {
      componentName: "FilingIdentity",
      service: "tax-filing/filing",
      route: "identity",
      slug: "Identity",
      icon: "identity",
    },
    {
      componentName: "FilingMessages",
      service: "tax-filing/filing",
      route: "messages",
      slug: "Chat",
      icon: "sms",
    },
    {
      componentName: "ProcessStatus",
      service: "tax-filing/filing",
      route: "process-status",
      slug: "Activity",
      icon: "trending_up",
    },
  ],
};

function Dashboard({
  service,
  matchedCpa: matchedCpaProp = null,
  loading = false,
}) {
  const dispatch = useDispatch(); // Added dispatch
  const location = useLocation();
  const params = useParams();
  const filingUuid = params.filingUuid;
  const socketRef = useRef(null);
  const socketIdentityRef = useRef(null);

  // State for new message badge
  const [hasNewMessages, setHasNewMessages] = useState(false);
  // State for new document badge
  const [hasNewDocuments, setHasNewDocuments] = useState(false);
  // State for matched CPA (fetched if not passed as prop)
  const [matchedCpa, setMatchedCpa] = useState(null);
  // State for client info (for CPA users)
  const [clientInfo, setClientInfo] = useState(null);
  // State for popups (filing dashboard specific)
  const [showCpaPopup, setShowCpaPopup] = useState(false);
  const [showClientPopup, setShowClientPopup] = useState(false);
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  // Check if user is CUS (taxpayer) or CPA
  const userRole = getUserRole();
  const isCus = userRole === "CUS";
  const isCpa = userRole === "CPA";

  // Update matchedCpa when prop changes
  useEffect(() => {
    if (matchedCpaProp) {
      setMatchedCpa(matchedCpaProp);
    }
  }, [matchedCpaProp]);

  // Fetch filing data (matched CPA for CUS users, client for CPA users)
  useEffect(() => {
    if (!filingUuid) return;

    // CUS: skip if already have matchedCpa from prop or state
    if (isCus && (matchedCpa || matchedCpaProp)) return;
    // CPA: skip if already have clientInfo
    if (isCpa && clientInfo) return;

    const fetchFilingData = async () => {
      try {
        const response = await fetchCurrentFiling("tax/filing", filingUuid);
        if (response.ok) {
          const data = await response.json();
          if (isCus && data.matched_cpa) {
            setMatchedCpa(data.matched_cpa);
          }
          if (isCpa && data.client) {
            setClientInfo(data.client);
          }
        }
      } catch (err) {
        console.error("Error fetching filing data:", err);
      }
    };

    fetchFilingData();
  }, [filingUuid, matchedCpa, matchedCpaProp, clientInfo, isCus, isCpa]);

  // Check if currently on messages page
  const isOnMessagesPage = location.pathname.includes("/messages");
  // Check if currently on filing summary (overview) page
  const isOnFilingSummary =
    location.pathname === `/tax-filing/filing/${filingUuid}` ||
    location.pathname === `/tax-filing/filing/${filingUuid}/` ||
    location.pathname === `/filing/${filingUuid}` ||
    location.pathname === `/filing/${filingUuid}/`;

  // Listen for new messages via WebSocket
  useEffect(() => {
    if (!filingUuid) return;

    // Create socket connection for this filing's chat
    socketRef.current = createWebSocketClient(
      `ws/chat/taxfiling/${filingUuid}`,
    );

    // Listen for socket identity
    socketRef.current.addListener("socket_identity", (data) => {
      socketIdentityRef.current = data.socket_identity;
    });

    // Listen for new messages
    socketRef.current.addListener(SocketActions.MESSAGE, (data) => {
      // Only show badge if message is from someone else and not on messages page
      if (socketIdentityRef.current !== data.socket_identity) {
        const currentlyOnMessages =
          window.location.pathname.includes("/messages");
        if (!currentlyOnMessages) {
          setHasNewMessages(true);
        }
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.removeListener(SocketActions.MESSAGE);
        socketRef.current.removeListener("socket_identity");
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [filingUuid]);

  // Clear badge when navigating to messages page
  useEffect(() => {
    if (isOnMessagesPage) {
      setHasNewMessages(false);
    }
  }, [isOnMessagesPage]);

  // Clear document badge when navigating to filing summary
  useEffect(() => {
    if (isOnFilingSummary) {
      setHasNewDocuments(false);
    }
  }, [isOnFilingSummary]);

  // Listen for document notifications via FilingNotificationService
  useEffect(() => {
    if (!filingUuid) return;

    const role = getUserRole();
    const socketPath =
      role === "CPA" ? "/ws/filing/provider" : "/ws/filing/taxpayer";

    const socketService = new FilingNotificationService(socketPath);
    const subscription = socketService.getMessages().subscribe((message) => {
      // The notification data is nested in message.message
      const notification = message?.message || message;

      // Check notification type from various possible fields
      const notificationType = (
        notification?.avatar ||
        notification?.type ||
        notification?.event_type ||
        notification?.notification_type ||
        ""
      ).toUpperCase();

      // Extract filing ID from the notification link (e.g., "filing/523c883a-891a-43fa-b4e5-591ffcd9efd9")
      const notificationLink = notification?.link || "";
      // Updated regex to match UUID format (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
      const linkMatch = notificationLink.match(/filing\/([a-f0-9-]+)/i);
      const messageFilingId = linkMatch
        ? linkMatch[1]
        : notification?.filing_id || notification?.filingUuid;

      const isDocumentNotification =
        notificationType === "SD" ||
        notificationType === "NEW DOCUMENT" ||
        notificationType === "DD" ||
        notificationType === "DOCUMENT_DELETED";

      // Only show badge if it's for this filing and not already on filing summary
      if (
        isDocumentNotification &&
        String(messageFilingId) === String(filingUuid)
      ) {
        const currentlyOnSummary =
          window.location.pathname === `/tax-filing/filing/${filingUuid}` ||
          window.location.pathname === `/tax-filing/filing/${filingUuid}/` ||
          window.location.pathname === `/filing/${filingUuid}` ||
          window.location.pathname === `/filing/${filingUuid}/`;
        if (!currentlyOnSummary) {
          setHasNewDocuments(true);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [filingUuid]);

  // Transform widgets to segments format for SegmentMenu
  // Wrapped in useMemo to prevent unnecessary Redux dispatches
  const segments = useMemo(() => {
    const widgets = serviceWidgetMap[service] || [];
    return widgets.map((widget) => {
      const widgetPath =
        widget.route === ""
          ? `/${widget.service}/${filingUuid}`
          : `/${widget.service}/${filingUuid}/${widget.route}`;

      const iconInfo = iconMap[widget.icon] || { normal: null, active: null };

      const segment = {
        path: widgetPath,
        text: widget.slug,
        normalIcon: iconInfo.normal,
        activeIcon: iconInfo.active,
      };

      // Add badge for Messages segment when there are new messages
      if (widget.route === "messages" && hasNewMessages) {
        segment.badge = "New";
        segment.onClick = () => setHasNewMessages(false);
      }

      // Add badge for Filing Summary segment when there are new documents
      if (widget.route === "" && hasNewDocuments) {
        segment.badge = "New";
        segment.onClick = () => setHasNewDocuments(false);
      }

      return segment;
    });
  }, [service, filingUuid, hasNewMessages, hasNewDocuments]);

  // Teleport Effect: Push segments to Redux for the bottom bar
  useEffect(() => {
    if (segments.length > 0) {
      dispatch(setSegments(segments));
    }

    return () => dispatch(clearSegments());
  }, [dispatch, segments]);

  // Determine current path for active segment
  const currentPath = location.pathname;

  // Build headerLeftContent for filing dashboard (CUS and CPA users)
  const headerLeftContent = (() => {
    // CUS users: show matched CPA
    if (isCus && matchedCpa) {
      return (
        <>
          {/* Matched CPA Avatar */}
          <div
            className="matched-cpa-container segment-header-user-container"
            onClick={() => setShowCpaPopup(!showCpaPopup)}
          >
            <div className="matched-cpa-avatar avatar-container">
              {matchedCpa.avatar ? (
                <img
                  src={matchedCpa.avatar}
                  alt={`${matchedCpa.first_name} ${matchedCpa.last_name}`}
                />
              ) : (
                <span>
                  {matchedCpa.first_name?.charAt(0)}
                  {matchedCpa.last_name?.charAt(0)}
                </span>
              )}
            </div>
            <span>
              {matchedCpa.first_name} {matchedCpa.last_name}
            </span>
          </div>

          {/* Help/Guidance Icon */}
          <div
            className="help-guidance-container guide-container"
            onClick={() => setShowHelpPopup(true)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/help_guidense_blue.svg`}
              alt="Help & Guidance"
            />
          </div>
        </>
      );
    }

    // CPA users: show client info
    if (isCpa && clientInfo) {
      return (
        <>
          {/* Client Avatar */}
          <div
            className="client-info-container"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              backgroundColor: "#f7f8f8",
              paddingRight: "18px",
              borderRadius: "50px",
            }}
            onClick={() => setShowClientPopup(!showClientPopup)}
          >
            <div
              className="client-avatar"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {clientInfo.avatar ? (
                <img
                  src={clientInfo.avatar}
                  alt={`${clientInfo.first_name} ${clientInfo.last_name}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#004789",
                  }}
                >
                  {clientInfo.first_name?.charAt(0)}
                  {clientInfo.last_name?.charAt(0)}
                </span>
              )}
            </div>
            <span
              style={{
                color: "#666B6F",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "120%",
              }}
            >
              {clientInfo.first_name} {clientInfo.last_name}
            </span>
          </div>

          {/* Help/Guidance Icon */}
          <div
            className="help-guidance-container"
            style={{
              width: "32px",
              height: "32px",
              padding: "8px 16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              aspectRatio: "1/1",
              borderRadius: "6px",
              background: "var(--secondary-50, #EBF5FF)",
              cursor: "pointer",
            }}
            onClick={() => setShowHelpPopup(true)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/help_guidense_blue.svg`}
              alt="Help & Guidance"
            />
          </div>
        </>
      );
    }

    return null;
  })();

  return (
    <>
      <div className="ich-wrapper">
        <div className="internal-content-header">
          <SegmentMenu
            loading={loading}
            segments={segments}
            currentPath={currentPath}
            headerLeftContent={headerLeftContent}
          />
        </div>
      </div>
      {/* CPA Profile Modal */}
      {showCpaPopup && matchedCpa && (
        <ModalNew className="content-modal" style={{ maxWidth: "480px" }}>
          <PopupHeader
            title="Your Tax Expert"
            setOpenState={() => setShowCpaPopup(false)}
            icon={`${process.env.PUBLIC_URL}/images/icons/friend_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "flex-start",
              }}
            >
              {/* Column 1: Avatar */}
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  minWidth: "100px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f0f7ff",
                }}
              >
                {matchedCpa.avatar ? (
                  <img
                    src={matchedCpa.avatar}
                    alt={`${matchedCpa.first_name} ${matchedCpa.last_name}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: "36px",
                      fontWeight: "600",
                      color: "#004789",
                    }}
                  >
                    {matchedCpa.first_name?.charAt(0)}
                    {matchedCpa.last_name?.charAt(0)}
                  </span>
                )}
              </div>

              {/* Column 2: Name, License, About Me */}
              <div style={{ flex: 1 }}>
                {/* Name */}
                <h3
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#1A2127",
                  }}
                >
                  {matchedCpa.first_name} {matchedCpa.last_name}
                </h3>

                {/* License Badge */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: "#e8f5e9",
                    padding: "4px 10px",
                    borderRadius: "4px",
                    marginBottom: "12px",
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="#2e7d32"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#2e7d32",
                    }}
                  >
                    Enrolled Agent
                  </span>
                </div>

                {/* About Me */}
                {matchedCpa.about_me && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#666",
                      lineHeight: "1.5",
                    }}
                  >
                    {matchedCpa.about_me}
                  </p>
                )}
              </div>
            </div>
          </PopupContent>
        </ModalNew>
      )}

      {/* Client Profile Modal (for CPA users) */}
      {showClientPopup && clientInfo && (
        <ModalNew className="content-modal" style={{ maxWidth: "480px" }}>
          <PopupHeader
            title="Client"
            setOpenState={() => setShowClientPopup(false)}
            icon={`${process.env.PUBLIC_URL}/images/icons/friend_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "flex-start",
              }}
            >
              {/* Column 1: Avatar */}
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  minWidth: "100px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f0f7ff",
                }}
              >
                {clientInfo.avatar ? (
                  <img
                    src={clientInfo.avatar}
                    alt={`${clientInfo.first_name} ${clientInfo.last_name}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: "36px",
                      fontWeight: "600",
                      color: "#004789",
                    }}
                  >
                    {clientInfo.first_name?.charAt(0)}
                    {clientInfo.last_name?.charAt(0)}
                  </span>
                )}
              </div>

              {/* Column 2: Name and About Me */}
              <div style={{ flex: 1 }}>
                {/* Name */}
                <h3
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#1A2127",
                  }}
                >
                  {clientInfo.first_name} {clientInfo.last_name}
                </h3>

                {/* About Me */}
                {clientInfo.about_me && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#666",
                      lineHeight: "1.5",
                    }}
                  >
                    {clientInfo.about_me}
                  </p>
                )}
              </div>
            </div>
          </PopupContent>
        </ModalNew>
      )}

      {/* Help & Guidance Modal */}
      {showHelpPopup && (
        <ModalNew className="content-modal" style={{ maxWidth: "480px" }}>
          <PopupHeader
            title="Help & Guidance"
            setOpenState={() => setShowHelpPopup(false)}
            icon={`${process.env.PUBLIC_URL}/images/icons/help_guidense_blue.svg`}
            variant="compact"
          />

          <PopupContent>
            {isCpa ? (
              <>
                <PopupSection title="Getting Started">
                  Welcome to your tax professional dashboard. Here you can
                  manage client filings, track progress, review submitted
                  documents, and communicate securely with your clients.
                </PopupSection>

                <PopupSection title="Reviewing Client Documents">
                  Click on any section marked with "Attention Needed" to see
                  which documents your client has uploaded and what additional
                  information may be required. You can request missing documents
                  directly through the platform.
                </PopupSection>

                <PopupSection title="Understanding Status Badges">
                  <p style={{ margin: 0 }}>
                    <strong style={{ color: "#40464B" }}>
                      Attention Needed:
                    </strong>{" "}
                    Action is required from you (review documents, prepare
                    forms, request clarifications).
                    <br />
                    <strong style={{ color: "#40464B" }}>Waiting:</strong> You
                    have completed your part and are waiting for client action.
                    <br />
                    <strong style={{ color: "#40464B" }}>Complete:</strong> This
                    section has been finalized and approved.
                  </p>
                </PopupSection>

                <PopupSection title="Preparing the Tax Return and E-File Workflow">
                  <p style={{ margin: 0 }}>
                    • Prepare the client's tax return and upload it for internal
                    review.
                    <br />
                    • Once all required documents and forms are accepted,
                    prepare Form 8879 (e-file authorization).
                    <br />
                    • Send the signature request to your client so they can
                    review and sign.
                    <br />
                    • Sign the 8879 last, after the client has completed their
                    part.
                    <br />• After submission to the IRS, upload the Proof of
                    Acceptance to finalize and close the filing process.
                  </p>
                </PopupSection>

                <PopupSection title="Communicating with Clients">
                  Use the Messages tab to send updates, request missing
                  information, or answer any questions. All communication is
                  secure and logged for reference.
                </PopupSection>

                <PopupSection title="Need More Help?" isLast>
                  Check the Help & Resources section for guides, FAQs, and best
                  practices for managing client filings.
                </PopupSection>
              </>
            ) : (
              <>
                <PopupSection title="Getting Started">
                  Welcome to your tax filing dashboard. Here you can track the
                  progress of your tax filing, upload required documents, and
                  communicate with your tax professional.
                </PopupSection>

                <PopupSection title="Uploading Documents">
                  Click on any section marked with "Attention Needed" to see
                  which documents are required. Use the upload button to submit
                  your documents securely.
                </PopupSection>

                <PopupSection title="Understanding Status Badges">
                  <p style={{ margin: 0 }}>
                    <strong style={{ color: "#40464B" }}>
                      Attention Needed:
                    </strong>{" "}
                    Action is required from you.
                    <br />
                    <strong style={{ color: "#40464B" }}>Waiting:</strong> Your
                    tax professional is working on this section.
                    <br />
                    <strong style={{ color: "#40464B" }}>Complete:</strong> This
                    section has been completed successfully.
                  </p>
                </PopupSection>

                <PopupSection title="E-File Authorization">
                  Once your tax professional prepares your return, you will be
                  asked to review and sign the e-file authorization form (Form
                  8879). This authorizes the electronic filing of your tax
                  return.
                </PopupSection>

                <PopupSection title="Need More Help?" isLast>
                  Use the Messages tab to communicate directly with your tax
                  professional. They are here to answer any questions you may
                  have about your tax filing.
                </PopupSection>
              </>
            )}
          </PopupContent>
        </ModalNew>
      )}
    </>
  );
}

export default Dashboard;
